import { z } from "zod";

const loose = z.record(z.string(), z.unknown());

export const EnvironmentLinkSchema = z.object({
  environmentId: z.string().optional(),
  environment_id: z.string().optional(),
  id: z.string().optional(),
  name: z.string().optional(),
  endpoint: loose.optional(),
  httpBaseUrl: z.string().optional(),
  http_base_url: z.string().optional(),
  url: z.string().optional(),
  wsBaseUrl: z.string().optional(),
  ws_base_url: z.string().optional(),
});
export type EnvironmentLink = Omit<z.infer<typeof EnvironmentLinkSchema>, "environmentId" | "httpBaseUrl"> & {
  environmentId: string;
  httpBaseUrl: string;
  wsBaseUrl?: string;
};

export const EnvironmentConnectSchema = z.object({
  environmentId: z.string(),
  endpoint: loose.optional(),
  credential: z.string(),
  expiresAt: z.string().optional(),
});

export const WebsocketTicketSchema = z.object({
  ticket: z.string(),
  expiresAt: z.string().optional(),
});

export const EnvironmentDescriptorSchema = z.object({
  environmentId: z.string(),
  label: z.string(),
  platform: loose,
  serverVersion: z.string(),
  capabilities: loose,
});

export const RpcExitSchema = z.object({
  _tag: z.literal("Exit"),
  requestId: z.number(),
  exit: loose,
});

export const EnvironmentEventSchema = z.object({
  type: z.string(),
  payload: z.unknown(),
});

export type EnvironmentEvent = z.infer<typeof EnvironmentEventSchema>;

export type Message = {
  role: string;
  text?: string;
  body?: string;
  createdAt?: string;
  time?: string;
  status?: string;
  [key: string]: unknown;
};

export type Thread = {
  id: string;
  projectId: string;
  title: string;
  modelSelection?: Record<string, unknown>;
  session?: Record<string, unknown>;
  messages?: Message[];
  updatedAt?: string;
  archivedAt?: string | null;
  [key: string]: unknown;
};

export type Project = {
  id: string;
  title: string;
  workspaceRoot?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

export type WorkspaceSnapshot = {
  snapshotSequence?: number;
  projects: Project[];
  threads: Thread[];
  updatedAt?: string;
  [key: string]: unknown;
};

export function parseSnapshot(value: unknown): WorkspaceSnapshot {
  const object = loose.parse(value);
  return {
    ...object,
    projects: Array.isArray(object.projects) ? (object.projects as Project[]) : [],
    threads: Array.isArray(object.threads) ? (object.threads as Thread[]) : [],
  };
}

export function normalizeEnvironmentLinks(value: unknown): EnvironmentLink[] {
  const raw = Array.isArray(value)
    ? value
    : loose.parse(value).environments ?? loose.parse(value).items ?? [];
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const parsed = EnvironmentLinkSchema.safeParse(entry);
    if (!parsed.success) return [];
    const endpoint = parsed.data.endpoint ?? {};
    const environmentId = parsed.data.environmentId ?? parsed.data.environment_id ?? parsed.data.id;
    const httpBaseUrl = parsed.data.httpBaseUrl ?? parsed.data.http_base_url ?? parsed.data.url ?? stringValue(endpoint.httpBaseUrl);
    if (!environmentId || !httpBaseUrl) return [];
    return [{
      ...parsed.data,
      environmentId,
      httpBaseUrl,
      wsBaseUrl: parsed.data.wsBaseUrl ?? parsed.data.ws_base_url ?? stringValue(endpoint.wsBaseUrl),
    }];
  });
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

export function rpcRequest(id: number, tag: string, payload: unknown): string {
  return JSON.stringify({ _tag: "Request", id, tag, payload, headers: [] });
}
